import {
  Button,
  type ColumnsType,
  ConfirmModal,
  Flex,
  Loading,
  Table,
  TableRowMenuCell,
  Text,
} from '@/components';
import { useTenantManagerPermission } from '@/features/authentication/_hooks/permission';
import { useMessage } from '@/providers';
import { TABLE_ROW_MENU_WIDTH } from '@/utils';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import type { HierarchyLabelModel } from '../..//models';
import {
  useCreateHierarchyLabel,
  useDeleteHierarchyLabel,
  useHierarchyLabels,
  useUpdateHierarchyLabel,
} from '../../hooks';
import { useHierarchyLabelsDrawerState } from './DrawerStateProvider';
import { HierarchyLabelFormDrawer } from './HierarchyLabelFormDrawer';
import { HierarchyLabelSortableTable } from './HierarchyLabelSortableTable';

export const HierarchyLabelsPage: FC = () => {
  const { t } = useTranslation();
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const { drawerState, setDrawerState } = useHierarchyLabelsDrawerState();
  const { openMessage } = useMessage();
  const hierarchyLabels = useHierarchyLabels();
  const { mutateAsync: createMutateAsync } = useCreateHierarchyLabel();
  const { mutateAsync: updateMutateAsync } = useUpdateHierarchyLabel();
  const { mutateAsync: deleteMutateAsync } = useDeleteHierarchyLabel();
  const editPermission = useTenantManagerPermission(true);
  const [hierarchyLabelDelete, setHierarchyLabelDelete] = useState<z.infer<
    typeof HierarchyLabelModel
  > | null>(null);

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        dataIndex: 'name',
        render: (value) => <Text weight="regular" text={value} />,
      },
      {
        title: t('hierarchy-labels.enabled-label'),
        key: 'isUsedForCommentOrWorkflow',
        render: (_, { isUsedForCommentOrWorkflow }) => (
          <Text
            weight="regular"
            text={
              isUsedForCommentOrWorkflow
                ? t('hierarchy-labels.enabled')
                : t('hierarchy-labels.disabled')
            }
          />
        ),
      },
      ...(editPermission.data
        ? [
            {
              title: '',
              key: 'actions',
              fixed: 'right',
              width: TABLE_ROW_MENU_WIDTH,
              onCell: () => ({
                style: { minWidth: TABLE_ROW_MENU_WIDTH },
              }),
              render: (_, record) => (
                <TableRowMenuCell
                  onEdit={() => {
                    setDrawerState({
                      open: true,
                      isEditing: true,
                      id: record.id,
                    });
                  }}
                  items={[
                    {
                      key: 'delete',
                      label: t('delete'),
                      danger: true,
                      onClick: () => setHierarchyLabelDelete(record),
                    },
                  ]}
                />
              ),
            },
          ]
        : []),
    ],
    [editPermission.data, setDrawerState, t],
  );

  if (hierarchyLabels.isLoading || !hierarchyLabels.data) {
    return <Loading />;
  }

  const selected = hierarchyLabels.data.find(
    (d) => drawerState.open && drawerState.isEditing && d.id === drawerState.id,
  );

  return (
    <>
      {editPermission.data && !isEditingOrder && (
        <Flex justify="flex-end" style={{ marginBottom: 12 }}>
          <Button
            onClick={() => {
              setIsEditingOrder((prev) => !prev);
            }}
          >
            {t('sort-order-edit')}
          </Button>
        </Flex>
      )}
      {isEditingOrder ? (
        <HierarchyLabelSortableTable
          dataSource={hierarchyLabels.data}
          columns={columns as ColumnsType<z.infer<typeof HierarchyLabelModel>>}
          onCancel={() => {
            setIsEditingOrder(false);
          }}
          onSubmitSuccess={() => {
            setIsEditingOrder(false);
          }}
        />
      ) : (
        <Table
          dataSource={hierarchyLabels.data}
          rowKey="id"
          columns={columns as ColumnsType<z.infer<typeof HierarchyLabelModel>>}
          pagination={false}
        />
      )}

      <HierarchyLabelFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              initialValues: {
                ...selected,
              },
            }
          : {})}
        onSubmit={async (values) => {
          invariant(drawerState.open, t('unreachable-code'));

          try {
            await (drawerState.isEditing
              ? updateMutateAsync({ ...values, id: drawerState.id })
              : createMutateAsync(values));

            openMessage('success', t('message-completed'));
            setDrawerState({ open: false });
          } catch {
            openMessage('error', t('message-save-failed'));
          }
        }}
      />
      <ConfirmModal
        open={hierarchyLabelDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(hierarchyLabelDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(hierarchyLabelDelete.id);
            Promise.all([
              openMessage('success', t('message-delete-completed')),
              setHierarchyLabelDelete(null),
            ]);
          } catch {
            openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setHierarchyLabelDelete(null)}
        skipCheck
      />
    </>
  );
};
